import {
   PatientContinuouslyUsedMedicine,
   PatientMedicine,
   PreciseDosage,
} from '@dreryk/egabinet-prescription-frontend-api';
import { MedicineSimple } from '@dreryk/drerykweb-medicine-api';

import { Choice } from '@dreryk/watermelon';

import {
   asPreciseDosageUnit,
   calculateNewIssuedToLocalDate,
   changeIssuedToDateFromPastToTodayOnly,
   ExtendedDosage,
   getIssuedFromDefaultValue,
   getQuantityDefaultValue,
   hasKnownPackageCapacity,
   isIssuedForMoreThanMonth,
   isUnderTreatmentSupervision,
   mapAdditionalEntitlementsToAdditionalEntitlementsTypes,
   mapReimbursementToPrescriptionReimbursementType,
   PatientReimbursement,
   PreciseDosageUnit,
   selectAdditionalRequirementsTypes,
   StandardPrescription,
} from '~libs.shared.prescription';

import { PatientId } from '../../../PrescriptionIssuingPanel.types';
import { mapAdditionalRequirementsToRequirementTypes } from '../../prescription/additionalRequirements/additionalRequirements.utils';
import { DraftPrescriptionDTO } from '../../prescriptions.types';

import { DraftStandardPrescription, DraftStandardPrescriptionFieldName } from './DraftStandardPrescriptionField.types';

export const createDraftStandardPrescriptionsFieldNameFactory = (index: number) => (
   key: keyof DraftStandardPrescription['data']
): DraftStandardPrescriptionFieldName => `draftPrescriptions[${index}].data.${key}`;

const getDosageUnitDefaultValue = (
   medicine: MedicineSimple,
   dosageUnit?: PreciseDosageUnit
): PreciseDosageUnit | null => {
   return dosageUnit ?? asPreciseDosageUnit(medicine.singlePackageUnit) ?? null;
};

export const mapPrescriptionToDraftStandardPrescription = (
   prescription: StandardPrescription,
   patientId: PatientId
): DraftStandardPrescription => {
   const issuedForYear = isIssuedForMoreThanMonth(prescription.issuedFrom, prescription.issuedTo);
   const issuedFrom = changeIssuedToDateFromPastToTodayOnly(prescription.issuedFrom);
   const issuedTo = calculateNewIssuedToLocalDate(issuedFrom, issuedForYear);
   return {
      type: 'standard',
      sourceType: 'fromServer',
      queryData: {
         medicineId: prescription.medicinePrescription.patientMedicine.medicine.id,
         patientId: patientId,
      },
      selectedByUser: {
         patientMedicine: prescription.medicinePrescription.patientMedicine, // TODO: DREGAB-3763 Return undefined after fix MedicinePrescriptionQuery
         patientReimbursement: undefined,
      },
      initialData: {
         additionalEntitlementTypes: prescription.additionalEntitlementTypes,
         medicinePrescription: undefined, // TODO: DREGAB-3763 Return prescription.medicinePrescription after fix MedicinePrescriptionQuery
         reimbursementType: prescription.reimbursementType,
         quantity: prescription.quantity,
         additionalRequirementsTypes: selectAdditionalRequirementsTypes({
            cito: false, // TODO: DREGAB-3969 Waiting for implementation - cito flag
            issuedForYear: issuedForYear,
            substituteForbidden: prescription.substituteForbidden,
         }),
         ruleValidationResults: prescription.ruleValidationResults,
      },
      data: {
         dosage: prescription.dosage ?? null,
         reimbursement: null,
         quantity: prescription.quantity,
         issuedFrom: issuedFrom,
         issuedTo: issuedTo,
         additionalEntitlements: null,
         additionalRequirements: null,
         numericalDosageFrequency: prescription.preciseDosage?.frequency ?? null,
         numericalDosageQuantity: prescription.preciseDosage?.portion ?? null,
         treatmentDaysCount: prescription.treatmentDays ?? null,
         dosageUnit: getDosageUnitDefaultValue(
            prescription.medicinePrescription.patientMedicine.medicine,
            asPreciseDosageUnit(prescription.preciseDosage?.unit)
         ),
         extendedDosage: prescription.extendedDosage ?? null,
      },
   };
};

export const mapPatientMedicineToDraftStandardPrescription = (
   patientId: PatientId,
   patientMedicine: PatientMedicine,
   patientReimbursement: PatientReimbursement | undefined
): DraftStandardPrescription => {
   const issuedForYear = false;
   const issuedFrom = getIssuedFromDefaultValue();
   const issuedTo = calculateNewIssuedToLocalDate(issuedFrom, issuedForYear);

   return {
      type: 'standard',
      sourceType: 'addedByUser',
      queryData: {
         medicineId: patientMedicine.medicine.id,
         patientId: patientId,
      },
      selectedByUser: {
         patientMedicine: patientMedicine,
         patientReimbursement: patientReimbursement,
      },
      initialData: {
         medicinePrescription: undefined,
         additionalEntitlementTypes: undefined,
         additionalRequirementsTypes: undefined,
         reimbursementType: undefined,
         quantity: getQuantityDefaultValue(),
         ruleValidationResults: undefined,
      },
      data: {
         dosage: null,
         reimbursement: null,
         quantity: getQuantityDefaultValue(),
         issuedFrom: issuedFrom,
         issuedTo: issuedTo,
         additionalEntitlements: null,
         additionalRequirements: null,
         numericalDosageFrequency: null,
         numericalDosageQuantity: null,
         treatmentDaysCount: null,
         dosageUnit: getDosageUnitDefaultValue(patientMedicine.medicine),
         extendedDosage: null,
      },
   };
};

export const mapPatientContinuouslyUsedMedicineToDraftStandardPrescription = (
   patientId: PatientId,
   patientMedicine: PatientMedicine,
   patientContinuouslyUsedMedicine: PatientContinuouslyUsedMedicine
): DraftStandardPrescription => {
   const draftStandardPrescription = mapPatientMedicineToDraftStandardPrescription(
      patientId,
      patientMedicine,
      undefined
   );

   if (patientContinuouslyUsedMedicine.dosage) {
      draftStandardPrescription.data.dosage = patientContinuouslyUsedMedicine.dosage;
   }

   if (patientContinuouslyUsedMedicine.quantity) {
      draftStandardPrescription.data.quantity = patientContinuouslyUsedMedicine.quantity;
   }

   if (patientContinuouslyUsedMedicine.reimbursementType) {
      draftStandardPrescription.initialData.reimbursementType = patientContinuouslyUsedMedicine.reimbursementType;
   }

   if (patientContinuouslyUsedMedicine.additionalEntitlementTypes) {
      draftStandardPrescription.initialData.additionalEntitlementTypes =
         patientContinuouslyUsedMedicine.additionalEntitlementTypes;
   }

   draftStandardPrescription.initialData.additionalRequirementsTypes = selectAdditionalRequirementsTypes({
      cito: false, // TODO: DREGAB-3969 Waiting for implementation - cito flag
      issuedForYear: false,
      substituteForbidden: !patientContinuouslyUsedMedicine.substituteAllowed,
   });

   return draftStandardPrescription;
};

export const mapDraftStandardPrescriptionToDraftPrescriptionDTO = (
   prescription: DraftStandardPrescription
): DraftPrescriptionDTO => {
   const additionalRequirementsTypes = mapAdditionalRequirementsToRequirementTypes(
      prescription.data.additionalRequirements
   );

   const substituteForbidden = additionalRequirementsTypes.includes('SUBSTITUTE_FORBIDDEN');
   const issuedForYear = additionalRequirementsTypes.includes('FOR_YEAR');
   const extendedDosageActive = Boolean(prescription.data.extendedDosage?.stages.length);
   return {
      additionalEntitlementTypes: mapAdditionalEntitlementsToAdditionalEntitlementsTypes(
         prescription.data.additionalEntitlements
      ),
      dosage: Choice.withUseDefaultIfNull(prescription.data.dosage),
      issuedFrom: prescription.data.issuedFrom!,
      issuedTo: prescription.data.issuedTo!,
      medicineId: prescription.queryData.medicineId,
      quantity: prescription.data.quantity ?? 0,
      reimbursementType: Choice.withUseDefaultIfNull(
         mapReimbursementToPrescriptionReimbursementType(prescription.data.reimbursement)
      ),
      substituteForbidden: substituteForbidden,
      cito: false, // TODO: DREGAB-3969 Waiting for implementation - cito flag,
      preciseDosage: Choice.withUseDefaultIfNull<PreciseDosage>(
         !extendedDosageActive &&
            prescription.data.numericalDosageFrequency &&
            prescription.data.numericalDosageQuantity &&
            prescription.data.dosageUnit
            ? {
                 frequency: prescription.data.numericalDosageFrequency ?? undefined,
                 portion: prescription.data.numericalDosageQuantity ?? undefined,
                 unit: prescription.data.dosageUnit,
              }
            : null
      ),
      treatmentDays: Choice.withUseDefaultIfNull(
         isUnderTreatmentSupervision({
            issuedForYear: issuedForYear,
            reimbursement: prescription.data.reimbursement,
            packageCapacityKnown: hasKnownPackageCapacity(prescription.selectedByUser.patientMedicine?.medicine),
         })
            ? prescription.data.treatmentDaysCount ?? null
            : null
      ),
      extendedDosage: Choice.withUseDefaultIfNull<ExtendedDosage>(prescription.data.extendedDosage),
   };
};
